const MIN_CHARGE_AMOUNT = 0.01;

function round(num) {
  return Math.round(num * 100) / 100;
}

export function requiresPayment(total) {
  const amount = Number(total);
  return Number.isFinite(amount) && amount >= MIN_CHARGE_AMOUNT;
}

export function cartToTicketLineItems(tiers, cart) {
  return tiers.flatMap((tier) => {
    const quantity = cart[tier.id] || 0;
    return Array.from({ length: quantity }, () => ({
      price: Number(tier.price) || 0,
      extraFee: Number(tier.extraFee) || 0,
    }));
  });
}

export function buildTicketFees(tickets, serviceCharge = 3.5, stripePercent = 0.029, stripeFixed = 0.3, hostPercent = 0) {
  const chargeable = tickets.filter((ticket) => ticket.price > 0);
  const ticketCharges = chargeable.reduce((sum, ticket) => sum + ticket.price, 0);
  if (ticketCharges === 0) {
    return { total: 0, breakdown: { tickets: 0, serviceCharges: 0, extraTicketFees: 0, processingFees: 0 }, tickets: [] };
  }

  const serviceCharges = chargeable.reduce((sum, ticket) => (
    sum + serviceCharge + ((ticket.price * hostPercent) / 100) + ticket.extraFee
  ), 0);
  const total = round((ticketCharges + serviceCharges + stripeFixed) / (1 - stripePercent));

  return {
    total,
    breakdown: {
      tickets: ticketCharges,
      serviceCharges,
      extraTicketFees: chargeable.reduce((sum, ticket) => sum + ticket.extraFee, 0),
      processingFees: round(total - ticketCharges - serviceCharges),
    },
  };
}

export function buildCartFees(tiers, cart, serviceCharge = 3.5, stripePercent = 0.029, stripeFixed = 0.3, hostPercent = 0) {
  return buildTicketFees(cartToTicketLineItems(tiers, cart), serviceCharge, stripePercent, stripeFixed, hostPercent);
}

export const EMPTY_CUSTOMER_INFO = {
  firstName: '',
  lastName: '',
  email: '',
  phone: '',
  termsAccepted: false,
};

const EMAIL_PATTERN = /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/;
const PHONE_DIGIT_LIMIT = 10;

export function formatPhoneInput(value, previous = '') {
  const previousDigits = String(previous).replace(/\D/g, '').slice(0, PHONE_DIGIT_LIMIT);
  let digits = String(value || '').replace(/\D/g, '');
  if (digits.length === 11 && digits.startsWith('1')) digits = digits.slice(1);
  if (String(value || '').length < String(previous).length && digits.length === previousDigits.length) {
    digits = previousDigits.slice(0, -1);
  }
  digits = digits.slice(0, PHONE_DIGIT_LIMIT);

  if (!digits) return '';
  if (digits.length < 4) return `(${digits}`;
  if (digits.length < 7) return `(${digits.slice(0, 3)}) ${digits.slice(3)}`;
  return `(${digits.slice(0, 3)}) ${digits.slice(3, 6)}-${digits.slice(6)}`;
}

export function validateCustomerInfo(info) {
  const errors = {};
  const firstName = (info?.firstName || '').trim();
  const lastName = (info?.lastName || '').trim();
  const email = (info?.email || '').trim();
  const phone = String(info?.phone || '').replace(/\D/g, '');

  if (firstName.length < 2) errors.firstName = firstName ? 'Enter at least 2 characters' : 'First name is required';
  if (lastName.length < 2) errors.lastName = lastName ? 'Enter at least 2 characters' : 'Last name is required';
  if (!email) errors.email = 'Email is required';
  else if (!EMAIL_PATTERN.test(email)) errors.email = 'Enter a valid email address';
  if (phone.length !== 10) errors.phone = phone ? 'Enter a 10-digit phone number' : 'Phone number is required';
  if (!info?.termsAccepted) errors.termsAccepted = 'You must agree to the terms and conditions';

  return {
    isValid: Object.keys(errors).length === 0,
    errors,
    values: { firstName, lastName, email, phone, termsAccepted: Boolean(info?.termsAccepted) },
  };
}

export function isTicketEventOwnedByCompany(event, company) {
  const eventCompanyId = event?.company?._id || event?.company;
  const currentCompanyId = company?._id;
  return Boolean(eventCompanyId && currentCompanyId && String(eventCompanyId) === String(currentCompanyId));
}

export function getVisibleInventory(ticketInventory, aff) {
  const inventory = Array.isArray(ticketInventory) ? ticketInventory : [];
  const activeInventory = inventory.filter((ticket) => (
    ticket?.active !== false && ticket?.deleted !== true && ticket?.seating_chart !== true
  ));
  const publicTickets = activeInventory.filter((ticket) => !ticket?.affiliate?.slug);
  if (!aff) return publicTickets;

  const affiliateTickets = activeInventory.filter((ticket) => ticket?.affiliate?.slug === aff);
  if (!affiliateTickets.length) return publicTickets;

  const seen = new Set();
  return [...affiliateTickets, ...publicTickets].filter((ticket) => {
    const key = ticket._id || ticket.ticketName;
    if (seen.has(key)) return false;
    seen.add(key);
    return true;
  });
}
